import { z } from 'zod'
import { assessCostCoverage, priceEvidence } from './cost-evidence'
import { CURRENCIES, type SimulationPublic } from './schemas/simulation'
import {
  countsTowardTasteModel,
  TASTING_SCORE_KEYS,
  type TastePrediction,
  type TastingScores,
  type TastingSource,
} from './schemas/tastings'
import {
  findUnsupportedClaims,
  SALES_COPY_FIELDS,
  UNSUPPORTED_CLAIM_KINDS,
  UNSUPPORTED_CLAIM_RULES_VERSION,
  type RemovedClaim,
} from './unsupported-claims'
import { priceBasisStatus } from './cost-evidence'
import type { StartGoals } from './schemas/simulation'
import type { TasteProfile } from './taste-axes'
import { scaleCheckSchema, type ScaleCheck } from './scale-check'
import { SOURCE_TAGS } from './source-type'

/**
 * 제품 검토 리포트 (C45, 2026-10-30). 저장된 결과와 이미 있는 기록만 모아 다섯 영역을 신호등으로 보이고, 고칠 것을 고른다.
 * AI 를 부르지 않는다 — 맛은 이미 받아 둔 AI 맛 추정(시식 전)과 시식 기록을 다시 쓰고, 원가는 결과의 원가·단가 근거를,
 * 판매 문구는 근거 없는 표현 규칙(unsupported-claims.ts, 효능·질병은 P8 규칙 그대로)을 쓴다. 그래서 비용·AI 동의가 따로 없다.
 * 영역마다 근거(evidence)에 근거 꼬리표(source-type.ts)를 붙인다: 사용자 입력 · 계산 · AI 추정 · 시식 기록 · 견적 · 미확인.
 * 문구는 코드로만 담고(앱이 4개 언어로 그린다), 결과에서 옮긴 말(걸린 표현·원료 이름)만 원문 그대로 둔다.
 * 서버가 한 번 만든 리포트는 고치지 않고 저장한다 (apps/api/src/lib/simulation-review-reports.ts).
 * 판단 기준을 바꾸면 PRODUCT_REVIEW_RULES_VERSION 을 올린다 — 저장된 리포트에 그때의 판이 남는다
 */
export const PRODUCT_REVIEW_RULES_VERSION = `review-v2+${UNSUPPORTED_CLAIM_RULES_VERSION}`

/**
 * 화면 순서 (대표 결정 K, 2026-10-30: 다섯 영역 대신 아홉 내용 모두). taste 맛 · target 목표와 차이 · cost 원가·수익 ·
 * checks 확인 필요 · claims 판매 문구 · safety 보관·안전 · production 생산 준비(발주 판단). '다음 행동'은 fixes(급한 순서),
 * 맨 위 '더 만들어도 남을까'는 scaleCheck 이다
 */
export const PRODUCT_REVIEW_AREAS = [
  'taste',
  'target',
  'cost',
  'checks',
  'claims',
  'safety',
  'production',
] as const
export type ProductReviewArea = (typeof PRODUCT_REVIEW_AREAS)[number]

/** 급한 순서. unknown = 판단할 기록이 아직 없음 (회색) */
export const PRODUCT_REVIEW_LIGHTS = ['red', 'yellow', 'unknown', 'green'] as const
export type ProductReviewLight = (typeof PRODUCT_REVIEW_LIGHTS)[number]

/** 근거 출처는 근거 꼬리표(source-type.ts) 하나를 쓴다. 기록이 없으면 unconfirmed(미확인) */
export const PRODUCT_REVIEW_SOURCES = SOURCE_TAGS

export const PRODUCT_REVIEW_EVIDENCE_KEYS = [
  'target_taste',
  'target_axis',
  'start_goal',
  'qty_needs_check',
  'unconfirmed_prices',
  'expired_prices',
  'ai_taste',
  'ai_smell',
  'ai_texture',
  'tasting_rounds',
  'tasting_taste',
  'tasting_smell',
  'tasting_texture',
  'tasting_overall',
  'cost_per_unit',
  'suggested_retail',
  'margin',
  'missing_prices',
  'estimated_prices',
  'claim',
  'removed_claim',
  'claims_checked',
  'storage',
  'shelf_life_days',
  'allergens',
  'storage_trial',
  'quote_stage',
  'factory_tastings',
] as const
export type ProductReviewEvidenceKey = (typeof PRODUCT_REVIEW_EVIDENCE_KEYS)[number]

/** 판매 문구 중 검사하는 칸 (claim 근거의 subject). 원료 역할은 표시만 하고 자동으로 빼지는 않는다 */
export const PRODUCT_REVIEW_TEXT_FIELDS = [...SALES_COPY_FIELDS, 'ingredient_role'] as const

export const PRODUCT_REVIEW_FIX_CODES = [
  'remove_health_claim',
  'remove_unsupported_claim',
  'add_missing_prices',
  'raise_margin',
  'confirm_estimated_prices',
  'improve_taste',
  'record_tasting',
  'get_taste_estimate',
  'verify_shelf_life',
  'choose_factory',
  'record_factory_sample',
  'request_quote',
  'set_target',
  'close_taste_gap',
  'confirm_quantities',
  'update_expired_prices',
] as const
export type ProductReviewFixCode = (typeof PRODUCT_REVIEW_FIX_CODES)[number]

/** 견적 진행 단계. 공장을 고른 견적 > 제안이 온 견적 > 접수만 된 견적 > 없음 (취소·마감된 견적은 세지 않는다) */
export const PRODUCT_REVIEW_QUOTE_STAGES = ['none', 'waiting', 'offers', 'factory_chosen'] as const
export type ProductReviewQuoteStage = (typeof PRODUCT_REVIEW_QUOTE_STAGES)[number]

/** 첫 화면 요약에 보이는 '고칠 것' 개수. 나머지는 영역을 펼치면 보인다 */
export const PRODUCT_REVIEW_SUMMARY_FIXES = 3

/**
 * 신호등 기준 (대표 결정 2026-10-30: 기본값은 여기 한 곳, 관리자 설정 settings.productReview.thresholds 로 바꿀 수 있다).
 * - marginRed·marginYellow: 제조원가 기준 추정 마진. 생성 검사(pipeline/gate.ts margin-positive)가 15% 를 넘어야
 *   통과시키므로 그 아래는 빨강, 30% 아래는 노랑
 * - tastingRed·tastingGreen: 시식 전체 만족도 평균(1~5). 3 미만 빨강, 4 이상 초록
 */
export const productReviewThresholdsSchema = z
  .object({
    marginRed: z.number().min(-1).max(1),
    marginYellow: z.number().min(-1).max(1),
    tastingRed: z.number().min(1).max(5),
    tastingGreen: z.number().min(1).max(5),
  })
  .refine((t) => t.marginRed <= t.marginYellow && t.tastingRed <= t.tastingGreen, {
    message: 'red must not exceed yellow/green',
  })
export type ProductReviewThresholds = z.infer<typeof productReviewThresholdsSchema>
export const PRODUCT_REVIEW_THRESHOLDS: ProductReviewThresholds = {
  marginRed: 0.15,
  marginYellow: 0.3,
  tastingRed: 3,
  tastingGreen: 4,
}
/** 제품마다 남기는 리포트 수 기본값 (관리자 설정 settings.productReview.keep) */
export const PRODUCT_REVIEW_KEEP = 20

const evidenceSchema = z
  .object({
    key: z.enum(PRODUCT_REVIEW_EVIDENCE_KEYS),
    source: z.enum(PRODUCT_REVIEW_SOURCES),
    value: z
      .union([z.string().max(200), z.number().finite(), z.array(z.string().max(60)).max(14)])
      .nullable(),
    /** claim: 걸린 칸(PRODUCT_REVIEW_TEXT_FIELDS), 원료 역할이면 그 원료 이름. target_axis: 맛 축 id. start_goal: 질문 */
    subject: z.string().max(100).nullable(),
    /** claim 만: 근거 없는 표현의 종류 */
    kind: z.enum(UNSUPPORTED_CLAIM_KINDS).nullable(),
  })
  .strict()
export type ProductReviewEvidence = z.infer<typeof evidenceSchema>

const fixSchema = z
  .object({
    area: z.enum(PRODUCT_REVIEW_AREAS),
    light: z.enum(PRODUCT_REVIEW_LIGHTS),
    code: z.enum(PRODUCT_REVIEW_FIX_CODES),
    /** 문구에 넣는 값: 걸린 표현(글), 개수·마진·점수·일수(수) */
    detail: z.union([z.string().max(200), z.number().finite()]).nullable(),
  })
  .strict()
export type ProductReviewFix = z.infer<typeof fixSchema>

export const productReviewBodySchema = z
  .object({
    schemaVersion: z.literal(1),
    rulesVersion: z.string().max(60),
    currency: z.enum(CURRENCIES),
    /** 이 리포트를 만들 때 쓴 신호등 기준 (관리자가 나중에 바꿔도 그때 기준이 남는다) */
    thresholds: productReviewThresholdsSchema,
    areas: z
      .array(
        z
          .object({
            id: z.enum(PRODUCT_REVIEW_AREAS),
            light: z.enum(PRODUCT_REVIEW_LIGHTS),
            evidence: z.array(evidenceSchema).max(60),
          })
          .strict(),
      )
      .length(PRODUCT_REVIEW_AREAS.length),
    /** 급한 것부터. 화면 첫 줄은 앞의 PRODUCT_REVIEW_SUMMARY_FIXES 개 */
    fixes: z.array(fixSchema).max(40),
    /** '더 만들어도 남을까' 첫 카드 (Top5 #1). 관리자 settings.scaleCheck 가 켜졌을 때만 서버가 채운다 */
    scaleCheck: scaleCheckSchema.optional(),
  })
  .strict()
export type ProductReviewBody = z.infer<typeof productReviewBodySchema>

/** POST /simulations/:id/review 응답의 report. 저장된 그대로 (같은 내용이면 처음 저장한 행을 돌려준다) */
export const productReviewSchema = productReviewBodySchema.extend({
  id: z.string().max(64),
  simulationId: z.string().max(64),
  /** 이 리포트가 본 저장본의 지문 (편집 동시성의 baseRevision 과 같은 값) */
  resultRevision: z.string().max(128),
  /** 그때의 배합 버전 (C42). 버전 기록이 꺼져 있었거나 이력 밖에서 바뀐 저장본이면 null */
  formulaVersionId: z.string().max(64).nullable(),
  createdAt: z.string().datetime(),
})
export type ProductReview = z.infer<typeof productReviewSchema>

export interface ProductReviewInput {
  result: SimulationPublic
  /** 설명 화면에서 고른 맛 칩 id (input.tastes) */
  targetTasteIds: readonly string[]
  /** 이 저장본에 대해 이미 받아 둔 AI 맛 추정(시식 전). 없으면 null — 리포트는 새로 추정하지 않는다 */
  tasteEstimate: TastePrediction | null
  tastings: readonly { source: TastingSource; scores: TastingScores }[]
  quoteStage: ProductReviewQuoteStage
  /** 생성·AI 수정이 판매 문구에서 자동으로 뺀 표현 (simulations.internal.removedClaims) */
  removedClaims: readonly RemovedClaim[]
  thresholds: ProductReviewThresholds
  /** 목표: 맛 고르기에서 고른 목표 맛 값(input.tasteProfile)과 시작 질문 답(input.startGoals). 없으면 null */
  targetProfile: TasteProfile | null
  startGoals: StartGoals | null
  /** 단가 유효기간 판정의 오늘 (YYYY-MM-DD) */
  today: string
  /** 첫 카드 (scale-check.ts 의 buildScaleCheck 로 만든 것). 꺼져 있으면 없음 */
  scaleCheck?: ScaleCheck
}

type Evidence = Omit<ProductReviewEvidence, 'subject' | 'kind'> &
  Partial<Pick<ProductReviewEvidence, 'subject' | 'kind'>>
type Fix = Omit<ProductReviewFix, 'detail'> & { detail?: ProductReviewFix['detail'] }
interface AreaReview {
  light: ProductReviewLight
  evidence: Evidence[]
  fixes: Omit<Fix, 'area'>[]
}

const average = (values: number[]) =>
  Math.round((values.reduce((sum, v) => sum + v, 0) / values.length) * 10) / 10

function tasteArea({ tasteEstimate, tastings, thresholds }: ProductReviewInput): AreaReview {
  const evidence: Evidence[] = []
  if (tasteEstimate) {
    for (const key of ['taste', 'smell', 'texture'] as const)
      evidence.push({
        key: `ai_${key}`,
        source: 'ai_estimated',
        value: tasteEstimate[key].prediction,
      })
  }
  if (tastings.length === 0) {
    evidence.push({ key: 'tasting_rounds', source: 'unconfirmed', value: 0 })
    return tasteEstimate
      ? { light: 'yellow', evidence, fixes: [{ light: 'yellow', code: 'record_tasting' }] }
      : { light: 'unknown', evidence, fixes: [{ light: 'unknown', code: 'get_taste_estimate' }] }
  }
  evidence.push({ key: 'tasting_rounds', source: 'sensory_panel', value: tastings.length })
  for (const key of TASTING_SCORE_KEYS)
    evidence.push({
      key: `tasting_${key}`,
      source: 'sensory_panel',
      value: average(tastings.map((t) => t.scores[key])),
    })
  const overall = average(tastings.map((t) => t.scores.overall))
  if (overall >= thresholds.tastingGreen) return { light: 'green', evidence, fixes: [] }
  const light = overall < thresholds.tastingRed ? 'red' : 'yellow'
  return { light, evidence, fixes: [{ light, code: 'improve_taste', detail: overall }] }
}

/** 목표 맛 단계와 결과 맛 단계가 이만큼 벌어지면 '목표와 차이'를 노랑으로 (1~5 단계 축) */
const TASTE_GAP_LEVELS = 2

/**
 * 목표와 차이. 사용자가 정한 목표(맛 칩·맛 축 목표·시작 질문 답)와 지금 결과를 나란히 둔다. 결과 쪽 맛 값은 AI 가 배합을 보고
 * 정한 추정이다. 1~5 단계 축만 차이를 잰다 (수치 목표·고르기 축은 보이기만)
 */
function targetArea({
  result,
  targetTasteIds,
  targetProfile,
  startGoals,
}: ProductReviewInput): AreaReview {
  const evidence: Evidence[] = []
  if (targetTasteIds.length)
    evidence.push({ key: 'target_taste', source: 'user_input', value: [...targetTasteIds] })
  const gaps: string[] = []
  for (const [axis, wanted] of Object.entries(targetProfile?.values ?? {})) {
    const actual = result.tasteProfile?.values[axis]
    evidence.push({
      key: 'target_axis',
      source: actual === undefined ? 'user_input' : 'ai_estimated',
      value: actual === undefined ? String(wanted) : `${wanted} → ${actual}`,
      subject: axis.slice(0, 100),
    })
    const isLevel = (v: unknown): v is number =>
      Number.isInteger(v) && Number(v) >= 1 && Number(v) <= 5
    if (isLevel(wanted) && isLevel(actual) && Math.abs(wanted - actual) >= TASTE_GAP_LEVELS)
      gaps.push(axis)
  }
  for (const [question, answer] of Object.entries(startGoals ?? {}))
    if (answer && answer !== 'unknown')
      evidence.push({ key: 'start_goal', source: 'user_input', value: answer, subject: question })
  if (evidence.length === 0) {
    evidence.push({ key: 'target_taste', source: 'unconfirmed', value: null })
    return { light: 'unknown', evidence, fixes: [{ light: 'unknown', code: 'set_target' }] }
  }
  if (gaps.length === 0) return { light: 'green', evidence, fixes: [] }
  return {
    light: 'yellow',
    evidence,
    fixes: gaps.map((axis) => ({
      light: 'yellow' as const,
      code: 'close_taste_gap' as const,
      detail: axis,
    })),
  }
}

/**
 * 확인 필요. 결과에 남아 있는 '아직 사람이 확인하지 않은 것'을 한곳에 센다: 대략 분량(C40), 추정·빠진 단가,
 * 유효기간이 지난 입력 단가(#6), 시식 기록, 보관 시험. 고칠 것은 다른 영역에 없는 것만 낸다 (같은 일을 두 번 시키지 않게)
 */
function checksArea({ result, tastings, today }: ProductReviewInput): AreaReview {
  const qty = result.ingredients.filter((i) => i.qtyStatus === 'needs_check').length
  const prices = [
    ...result.ingredients.map((i) => priceEvidence(i.unitPrice, i.priceEvidence)),
    priceEvidence(result.costs.packagingPerUnit, result.costs.evidence?.packaging),
    priceEvidence(result.costs.laborPerUnit, result.costs.evidence?.labor),
  ]
  const unconfirmed = prices.filter((p) => p.status !== 'provided').length
  const expired = result.ingredients.filter(
    (i) =>
      priceEvidence(i.unitPrice, i.priceEvidence).source === 'user_input' &&
      priceBasisStatus(i.priceBasis, today) === 'expired',
  ).length
  const evidence: Evidence[] = [
    { key: 'qty_needs_check', source: 'calculated', value: qty },
    { key: 'unconfirmed_prices', source: 'calculated', value: unconfirmed },
    { key: 'expired_prices', source: 'calculated', value: expired },
    tastings.length
      ? { key: 'tasting_rounds', source: 'sensory_panel', value: tastings.length }
      : { key: 'tasting_rounds', source: 'unconfirmed', value: 0 },
    { key: 'storage_trial', source: 'unconfirmed', value: null },
  ]
  const fixes: AreaReview['fixes'] = []
  if (qty > 0) fixes.push({ light: 'yellow', code: 'confirm_quantities', detail: qty })
  if (expired > 0) fixes.push({ light: 'yellow', code: 'update_expired_prices', detail: expired })
  // 보관 시험 기록이 없어서 늘 확인할 것이 남는다 — 노랑
  return { light: 'yellow', evidence, fixes }
}

function costArea({ result, thresholds }: ProductReviewInput): AreaReview {
  const coverage = assessCostCoverage(result)
  const prices = [
    ...result.ingredients.map((i) => priceEvidence(i.unitPrice, i.priceEvidence)),
    priceEvidence(result.costs.packagingPerUnit, result.costs.evidence?.packaging),
    priceEvidence(result.costs.laborPerUnit, result.costs.evidence?.labor),
  ]
  const missing = prices.filter((p) => p.status === 'missing').length
  const estimated = prices.filter((p) => p.status === 'estimated').length
  const margin = result.pricing.marginPct
  const evidence: Evidence[] = [
    coverage.complete
      ? {
          key: 'cost_per_unit',
          source: 'calculated',
          value: result.costs.totalPerUnit,
        }
      : { key: 'cost_per_unit', source: 'unconfirmed', value: null },
    // 제안 판매가는 AI 가 시장가를 보고 정한 초안이다 (실제 판매 기록이 아니다)
    { key: 'suggested_retail', source: 'ai_estimated', value: result.pricing.suggestedRetail },
    coverage.complete
      ? { key: 'margin', source: 'calculated', value: margin }
      : { key: 'margin', source: 'unconfirmed', value: null },
    { key: 'missing_prices', source: 'calculated', value: missing },
    { key: 'estimated_prices', source: 'calculated', value: estimated },
  ]
  const fixes: AreaReview['fixes'] = []
  // 단가가 빠지면 원가·마진 자체를 믿을 수 없으니 마진은 보지 않는다
  if (missing > 0) fixes.push({ light: 'red', code: 'add_missing_prices', detail: missing })
  else if (margin < thresholds.marginYellow)
    fixes.push({
      light: margin < thresholds.marginRed ? 'red' : 'yellow',
      code: 'raise_margin',
      detail: margin,
    })
  if (estimated > 0)
    fixes.push({ light: 'yellow', code: 'confirm_estimated_prices', detail: estimated })
  return { light: worst(fixes.map((f) => f.light)), evidence, fixes }
}

function claimsArea({ result, removedClaims }: ProductReviewInput): AreaReview {
  const fields: { subject: string; text: string }[] = [
    { subject: 'name', text: result.product.name },
    { subject: 'tagline', text: result.product.tagline },
    { subject: 'how_to_use', text: result.product.howToUse },
    ...result.plainSteps.map((text) => ({ subject: 'plain_step', text })),
    ...result.ingredients.map((i) => ({ subject: `ingredient_role:${i.name}`, text: i.fn })),
  ]
  const evidence: Evidence[] = []
  const fixes: AreaReview['fixes'] = []
  const seen = new Set<string>()
  for (const field of fields) {
    for (const claim of findUnsupportedClaims(field.text)) {
      evidence.push({
        key: 'claim',
        source: 'ai_estimated',
        value: claim.phrase.slice(0, 200),
        subject: field.subject.slice(0, 100),
        kind: claim.kind,
      })
      if (seen.has(claim.phrase)) continue
      seen.add(claim.phrase)
      // 효능·질병과 인증 단정은 표시광고 위반이 될 수 있어 빨강, 확률·맛 점수는 근거를 남기거나 지울 것이라 노랑
      fixes.push({
        light: claim.kind === 'health' || claim.kind === 'certification' ? 'red' : 'yellow',
        code: claim.kind === 'health' ? 'remove_health_claim' : 'remove_unsupported_claim',
        detail: claim.phrase.slice(0, 200),
      })
    }
  }
  if (evidence.length === 0)
    evidence.push({ key: 'claims_checked', source: 'calculated', value: fields.length })
  // 생성·AI 수정이 이미 걷어 낸 표현은 기록으로만 보인다 (지금 문구에는 없으니 신호등·고칠 것에 넣지 않는다)
  for (const removed of removedClaims)
    evidence.push({
      key: 'removed_claim',
      source: 'calculated',
      value: removed.phrase,
      subject: removed.field,
      kind: removed.kind,
    })
  return { light: worst(fixes.map((f) => f.light)), evidence: evidence.slice(0, 60), fixes }
}

function safetyArea({ result }: ProductReviewInput): AreaReview {
  // 보관 방법·소비기한·알레르기는 생성 때 AI 가 정한 초안이고, 보관 시험 기록을 남기는 곳이 아직 없다 — 늘 노랑
  return {
    light: 'yellow',
    evidence: [
      { key: 'storage', source: 'ai_estimated', value: result.product.storage },
      { key: 'shelf_life_days', source: 'ai_estimated', value: result.product.shelfLifeDays },
      { key: 'allergens', source: 'ai_estimated', value: result.allergens.slice(0, 14) },
      { key: 'storage_trial', source: 'unconfirmed', value: null },
    ],
    fixes: [{ light: 'yellow', code: 'verify_shelf_life', detail: result.product.shelfLifeDays }],
  }
}

function productionArea({ quoteStage, tastings }: ProductReviewInput): AreaReview {
  const factoryTastings = tastings.filter((t) => countsTowardTasteModel(t.source)).length
  const evidence: Evidence[] = [
    quoteStage === 'none'
      ? { key: 'quote_stage', source: 'unconfirmed', value: null }
      : { key: 'quote_stage', source: 'supplier_quote', value: quoteStage },
    { key: 'factory_tastings', source: 'sensory_panel', value: factoryTastings },
  ]
  if (quoteStage === 'none')
    return { light: 'unknown', evidence, fixes: [{ light: 'unknown', code: 'request_quote' }] }
  if (quoteStage !== 'factory_chosen')
    return { light: 'yellow', evidence, fixes: [{ light: 'yellow', code: 'choose_factory' }] }
  if (factoryTastings === 0)
    return {
      light: 'yellow',
      evidence,
      fixes: [{ light: 'yellow', code: 'record_factory_sample' }],
    }
  return { light: 'green', evidence, fixes: [] }
}

const rank = (light: ProductReviewLight) => PRODUCT_REVIEW_LIGHTS.indexOf(light)
function worst(lights: ProductReviewLight[]): ProductReviewLight {
  return lights.reduce<ProductReviewLight>((a, b) => (rank(b) < rank(a) ? b : a), 'green')
}

const AREA_REVIEWS: Record<ProductReviewArea, (input: ProductReviewInput) => AreaReview> = {
  taste: tasteArea,
  target: targetArea,
  cost: costArea,
  checks: checksArea,
  claims: claimsArea,
  safety: safetyArea,
  production: productionArea,
}

/** 고칠 것을 고르는 순서: 신호등이 급한 것 → 판매 문구 → 원가 → 맛 → 보관 → 생산 (팔기 전에 막히는 것부터) */
const FIX_AREA_ORDER: readonly ProductReviewArea[] = [
  'claims',
  'cost',
  'checks',
  'taste',
  'target',
  'safety',
  'production',
]

/** 입력만으로 정해지는 순수 함수 — 같은 입력이면 같은 리포트 (저장 때 같은 내용을 한 번만 남기는 근거) */
export function buildProductReview(input: ProductReviewInput): ProductReviewBody {
  const reviews = PRODUCT_REVIEW_AREAS.map((id) => ({ id, ...AREA_REVIEWS[id](input) }))
  const fixes = reviews
    .flatMap(({ id, fixes: areaFixes }) => areaFixes.map((fix) => ({ area: id, ...fix })))
    .map((fix, index) => ({ fix, index }))
    .sort(
      (a, b) =>
        rank(a.fix.light) - rank(b.fix.light) ||
        FIX_AREA_ORDER.indexOf(a.fix.area) - FIX_AREA_ORDER.indexOf(b.fix.area) ||
        a.index - b.index,
    )
    .map(({ fix }) => ({ ...fix, detail: fix.detail ?? null }))
  return productReviewBodySchema.parse({
    schemaVersion: 1,
    rulesVersion: PRODUCT_REVIEW_RULES_VERSION,
    currency: input.result.currency,
    thresholds: input.thresholds,
    areas: reviews.map(({ id, light, evidence }) => ({
      id,
      light,
      evidence: evidence.map((e) => ({ subject: null, kind: null, ...e })),
    })),
    fixes: fixes.slice(0, 40),
    ...(input.scaleCheck ? { scaleCheck: input.scaleCheck } : {}),
  })
}
